import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { demoDataGenerator } from '../../utils/demoDataGenerator';

const PreviewNotifications = () => {
  const navigate = useNavigate();
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  
  const demoData = useMemo(() => demoDataGenerator?.getNotifications(), []);
  const { notifications, stats } = demoData;

  const filteredNotifications = selectedSeverity === 'all'
    ? notifications
    : notifications?.filter(n => n?.severity === selectedSeverity);

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'compliance': return 'Shield';
      case 'environmental': return 'CloudRain';
      case 'treatment': return 'Droplet';
      case 'audit': return 'FileText';
      case 'team': return 'Users';
      default: return 'Bell';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Eye" size={24} />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode - Notification Center</h3>
                <p className="text-sm text-orange-100">Sign up to receive real-time alerts and notifications for your operation</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="bg-white text-orange-600 hover:bg-orange-50 border-white"
                onClick={() => navigate('/pricing-page')}
              >
                View Plans
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Unified Notification Center</h1>
          <p className="text-gray-600 mt-2">Stay informed with real-time alerts and notifications</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Total</div>
            <div className="text-2xl font-bold text-gray-900">{stats?.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Unread</div>
            <div className="text-2xl font-bold text-orange-600">{stats?.unacknowledged}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Critical</div>
            <div className="text-2xl font-bold text-red-600">{stats?.critical}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">High</div>
            <div className="text-2xl font-bold text-orange-600">{stats?.high}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Medium</div>
            <div className="text-2xl font-bold text-yellow-600">{stats?.medium}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 mb-1">Low</div>
            <div className="text-2xl font-bold text-blue-600">{stats?.low}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-2 overflow-x-auto">
          {['all', 'critical', 'high', 'medium', 'low']?.map(severity => (
            <button
              key={severity}
              onClick={() => setSelectedSeverity(severity)}
              className={`px-4 py-2 rounded-lg font-medium capitalize whitespace-nowrap transition-colors ${
                selectedSeverity === severity
                  ? 'bg-orange-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {severity}
              {severity !== 'all' && (
                <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                  {stats?.[severity]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow divide-y divide-gray-200">
          {filteredNotifications?.map(notification => (
            <div
              key={notification?.id}
              className={`p-6 hover:bg-gray-50 transition-colors ${
                !notification?.isAcknowledged ? 'bg-blue-50/30' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                  notification?.severity === 'critical' ? 'bg-red-100' :
                  notification?.severity === 'high' ? 'bg-orange-100' :
                  notification?.severity === 'medium'? 'bg-yellow-100' : 'bg-blue-100'
                }`}>
                  <Icon
                    name={getTypeIcon(notification?.type)}
                    size={24}
                    className={`${
                      notification?.severity === 'critical' ? 'text-red-600' :
                      notification?.severity === 'high' ? 'text-orange-600' :
                      notification?.severity === 'medium'? 'text-yellow-600' : 'text-blue-600'
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{notification?.title}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full border ${getSeverityColor(notification?.severity)}`}>
                        {notification?.severity}
                      </span>
                      {notification?.actionRequired && (
                        <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                          Action Required
                        </span>
                      )}
                      {!notification?.isAcknowledged && (
                        <span className="w-2 h-2 bg-blue-600 rounded-full" />
                      )}
                    </div>
                    <span className="text-sm text-gray-500 whitespace-nowrap">
                      {formatTimestamp(notification?.timestamp)}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-3">{notification?.message}</p>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700 capitalize">
                      {notification?.type}
                    </span>
                    {notification?.isAcknowledged && (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Icon name="CheckCircle" size={14} />
                        Acknowledged
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-8 border-2 border-orange-200">
          <div className="text-center max-w-2xl mx-auto">
            <Icon name="Bell" size={48} className="text-orange-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Never Miss Critical Alerts</h3>
            <p className="text-gray-600 mb-6">
              Sign up to receive real-time notifications for compliance issues, environmental alerts, treatment reminders, and team updates. Stay ahead of problems before they escalate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white"
                onClick={() => navigate('/login')}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-orange-600 text-orange-600 hover:bg-orange-50"
                onClick={() => navigate('/pricing-page')}
              >
                View All Features
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">✓ Real-time alerts • ✓ SMS notifications • ✓ Email digests • ✓ Mobile app</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewNotifications;